import { ReactNode, useRef, useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";

interface HorizontalScrollTableProps {
  /** Table content */
  children: ReactNode;
  /** Minimum table width (triggers horizontal scroll) */
  minWidth?: number;
}

/**
 * Wrapper for tables that need horizontal scroll on mobile.
 * Provides:
 * - Smooth horizontal scrolling with momentum
 * - Visual gradient indicators showing scroll availability
 * - Accessible scroll behavior
 *
 * Best for admin tables with many columns that shouldn't be
 * converted to mobile cards.
 *
 * @example
 * <HorizontalScrollTable minWidth={800}>
 *   <Table>...</Table>
 * </HorizontalScrollTable>
 */
export function HorizontalScrollTable({
  children,
  minWidth = 650,
}: HorizontalScrollTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial check
    updateScrollIndicators();

    // Update on scroll
    el.addEventListener("scroll", updateScrollIndicators, { passive: true });

    // Update on resize
    const resizeObserver = new ResizeObserver(updateScrollIndicators);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollIndicators);
      resizeObserver.disconnect();
    };
  }, [updateScrollIndicators]);

  return (
    <Box sx={{ position: "relative" }}>
      {/* Left scroll indicator */}
      {canScrollLeft && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background:
              "linear-gradient(to right, rgba(255,255,255,0.95), transparent)",
            pointerEvents: "none",
            zIndex: 1,
          }}
          aria-hidden="true"
        />
      )}

      {/* Scroll container */}
      <Box
        ref={scrollRef}
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          // Hide scrollbar on mobile for cleaner look, show on desktop for accessibility
          scrollbarWidth: { xs: "none", md: "auto" },
          "&::-webkit-scrollbar": {
            display: { xs: "none", md: "block" },
          },
        }}
        role="region"
        aria-label="Scrollable table"
        tabIndex={0}
      >
        <Box sx={{ minWidth }}>{children}</Box>
      </Box>

      {/* Right scroll indicator */}
      {canScrollRight && (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background:
              "linear-gradient(to left, rgba(255,255,255,0.95), transparent)",
            pointerEvents: "none",
            zIndex: 1,
          }}
          aria-hidden="true"
        />
      )}
    </Box>
  );
}
